import { BADGE_COLOURS } from '../../../../../common/badges/badge-colours';

export type CertificateLayout = 'classic' | 'band' | 'minimal' | 'custom';
export type CertificateOrientation = 'landscape' | 'portrait';
export type FieldAlign = 'left' | 'center' | 'right';

export interface CertificateAsset {
  key: string;
  url: string;
}

export interface Signatory {
  name: string;
  title: string;
  image: CertificateAsset | null;
}

export interface CertificateField {
  id: string;
  key: string;
  text: string;
  x: number;
  y: number;
  width: number;
  size: number;
  colour: string;
  align: FieldAlign;
  font: string;
  bold: boolean;
}

export interface CertificateDesign {
  layout: CertificateLayout;
  orientation: CertificateOrientation;
  colour: string;
  showLogo: boolean;
  headingFont: string;
  bodyFont: string;
  title: string;
  body: string;
  signatories: Signatory[];
  background: CertificateAsset | null;
  fields: CertificateField[];
}

export interface CertificateTemplateDraft {
  certificateTemplateId: string | null;
  name: string;
  design: CertificateDesign;
}

export const CERTIFICATE_LAYOUTS: Record<CertificateLayout, string> = {
  classic: 'Classic',
  band: 'Band',
  minimal: 'Minimal',
  custom: 'Your design',
};

export const CERTIFICATE_FONTS: { key: string; label: string; css: string }[] = [
  { key: 'roboto', label: 'Roboto', css: "'Roboto', sans-serif" },
  { key: 'open-sans', label: 'Open Sans', css: "'Open Sans', sans-serif" },
  { key: 'montserrat', label: 'Montserrat', css: "'Montserrat', sans-serif" },
  { key: 'dejavu-sans', label: 'DejaVu Sans', css: "'DejaVu Sans', Verdana, sans-serif" },
  { key: 'eb-garamond', label: 'EB Garamond', css: "'EB Garamond', serif" },
  { key: 'playfair-display', label: 'Playfair Display', css: "'Playfair Display', serif" },
  { key: 'libre-baskerville', label: 'Libre Baskerville', css: "'Libre Baskerville', serif" },
  { key: 'dejavu-serif', label: 'DejaVu Serif', css: "'DejaVu Serif', Georgia, serif" },
  { key: 'cinzel', label: 'Cinzel (titles)', css: "'Cinzel', serif" },
  { key: 'great-vibes', label: 'Great Vibes (names)', css: "'Great Vibes', cursive" },
];

export const CERTIFICATE_FONTS_CSS =
  'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700&family=EB+Garamond:wght@400;700' +
  '&family=Great+Vibes&family=Libre+Baskerville:wght@400;700&family=Montserrat:wght@400;700' +
  '&family=Open+Sans:wght@400;700&family=Playfair+Display:wght@400;700&display=swap';

export const loadCertificateFonts = () => {
  if (document.getElementById('certificate-fonts')) return;
  const link = document.createElement('link');
  link.id = 'certificate-fonts';
  link.rel = 'stylesheet';
  link.href = CERTIFICATE_FONTS_CSS;
  document.head.appendChild(link);
};

export const fontCss = (key: string) =>
  CERTIFICATE_FONTS.find((font) => font.key === key)?.css ?? CERTIFICATE_FONTS[0].css;

export const CERTIFICATE_COLOURS = [
  'violet',
  'ultraviolet',
  'brunswick',
  'forest',
  'pacific',
  'midnight',
  'mulberry',
  'rose',
  'tangerine',
  'gold',
  'bronze',
  'slate',
].map((key) => ({ key, name: BADGE_COLOURS[key].name, hex: BADGE_COLOURS[key].base }));

export const MERGE_FIELDS = [
  'VOLUNTEER_NAME',
  'AWARDED_FOR',
  'DATE_AWARDED',
  'ORG_NAME',
  'ACHIEVEMENT',
  'LEVEL',
  'VALUE',
];

export const FIELD_LABELS: Record<string, string> = {
  VOLUNTEER_NAME: 'Volunteer name',
  AWARDED_FOR: 'Awarded for',
  DATE_AWARDED: 'Date awarded',
  ORG_NAME: 'Organisation name',
  ACHIEVEMENT: 'Achievement',
  LEVEL: 'Level',
  VALUE: 'Value',
  TEXT: 'Custom text',
  SIGNATURE_1: 'Signature 1',
  SIGNATURE_2: 'Signature 2',
};

export const IMAGE_FIELDS = ['SIGNATURE_1', 'SIGNATURE_2'];

export const pageSize = (orientation: CertificateOrientation) =>
  orientation === 'landscape' ? { width: 297, height: 210 } : { width: 210, height: 297 };

let fieldCounter = 0;
export const newFieldId = () => {
  fieldCounter += 1;
  return `field-${Date.now()}-${fieldCounter}`;
};

export const newField = (key: string, orientation: CertificateOrientation): CertificateField => {
  const page = pageSize(orientation);
  const image = IMAGE_FIELDS.includes(key);
  const width = image ? 30 : 160;
  return {
    id: newFieldId(),
    key,
    text: key === 'TEXT' ? 'Your text' : '',
    x: Math.round((page.width - width) / 2),
    y: Math.round(page.height / 2 - 10),
    width,
    size: key === 'VOLUNTEER_NAME' ? 28 : 14,
    colour: '#222222',
    align: 'center',
    font: key === 'VOLUNTEER_NAME' ? 'dejavu-serif' : 'dejavu-sans',
    bold: key === 'VOLUNTEER_NAME',
  };
};

const defaultDesign = (): CertificateDesign => ({
  layout: 'classic',
  orientation: 'landscape',
  colour: 'violet',
  showLogo: true,
  headingFont: 'dejavu-serif',
  bodyFont: 'dejavu-sans',
  title: 'Certificate of appreciation',
  body:
    'This certifies that\n{{VOLUNTEER_NAME}}\nis recognised for {{AWARDED_FOR}}\nawarded by {{ORG_NAME}} on {{DATE_AWARDED}}',
  signatories: [],
  background: null,
  fields: [],
});

export const emptyTemplateDraft = (): CertificateTemplateDraft => ({
  certificateTemplateId: null,
  name: '',
  design: defaultDesign(),
});

const parseJson = (json: string | null | undefined) => {
  try {
    return json ? JSON.parse(json) : {};
  } catch (e) {
    return {};
  }
};

export const draftFromTemplate = (template: {
  certificateTemplateId: string;
  name: string;
  design: string;
}): CertificateTemplateDraft => {
  const parsed = parseJson(template.design);
  const base = defaultDesign();
  return {
    certificateTemplateId: template.certificateTemplateId,
    name: template.name,
    design: {
      ...base,
      ...parsed,
      signatories: Array.isArray(parsed.signatories)
        ? parsed.signatories.map((item: Partial<Signatory>) => ({
            name: String(item?.name ?? ''),
            title: String(item?.title ?? ''),
            image: item?.image ?? null,
          }))
        : [],
      fields: Array.isArray(parsed.fields)
        ? parsed.fields
            .filter((field: Omit<CertificateField, 'id'>) => field.key in FIELD_LABELS)
            .map((field: Omit<CertificateField, 'id'>) => ({ ...field, id: newFieldId() }))
        : [],
    },
  };
};

export const toTemplateRequest = (draft: CertificateTemplateDraft) => ({
  certificateTemplateId: draft.certificateTemplateId,
  name: draft.name.trim(),
  design: {
    ...draft.design,
    title: draft.design.title.trim(),
    signatories: draft.design.signatories.filter((signatory) => signatory.name.trim()),
    fields: draft.design.fields.map(({ id: _unused, ...field }) => field),
  },
});

export const describeTemplate = (draft: CertificateTemplateDraft) => {
  const { design } = draft;
  const signatures = design.signatories.length;
  return [
    design.orientation === 'landscape' ? 'Landscape' : 'Portrait',
    design.layout === 'custom' ? 'your own design' : `${CERTIFICATE_LAYOUTS[design.layout].toLowerCase()} layout`,
    signatures ? `${signatures} signature${signatures === 1 ? '' : 's'}` : 'no signatures',
  ].join(', ');
};
